import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, Download, Send, ShieldCheck, XCircle } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, API } from "@/lib/api";

const PRIVACY_URL = "https://www.periziafirma.it/privacy-policy/";

export default function FascicoloSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const caseId = params.get("case_id");
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);
  const [privacyRead, setPrivacyRead] = useState(false);
  const [art9Consent, setArt9Consent] = useState(false);
  const [choice, setChoice] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [done, setDone] = useState(false);
  const [confirmPurge, setConfirmPurge] = useState(false);

  useEffect(() => {
    if (!sessionId || !caseId) { setStatus("error"); return; }
    let cancelled = false;
    const poll = async (n) => {
      if (cancelled) return;
      try {
        const r = await api.get(`/cases/${caseId}/fascicolo-status/${sessionId}`);
        if (r.data.payment_status === "paid") { setStatus("paid"); return; }
      } catch {}
      if (n >= 30) { setStatus("timeout"); return; }
      setAttempts(n + 1);
      setTimeout(() => poll(n + 1), 2000);
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId, caseId]);

  const validate = () => {
    if (!privacyRead) { toast.error("Conferma la lettura dell'informativa privacy"); return false; }
    if (!art9Consent) { toast.error("È richiesto il consenso al trattamento dei dati particolari (art. 9 GDPR)"); return false; }
    return true;
  };

  const downloadPdf = async () => {
    // FASE C.2 — intercept 409 (consistency block) before triggering a normal navigation.
    try {
      const res = await fetch(`${API}/cases/${caseId}/fascicolo-download/${sessionId}`, { credentials: "include" });
      if (res.status === 409) {
        const body = await res.json().catch(() => ({}));
        toast.error(body?.detail?.message
          || "Il fascicolo presenta incoerenze da risolvere prima della generazione del report.");
        return;
      }
      if (!res.ok) { toast.error("Errore durante il download"); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fascicolo-${caseId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      toast.error("Errore durante il download");
    }
  };

  const chooseShareAndDownload = async () => {
    if (!validate()) return;
    setProcessing(true); setChoice("share");
    try {
      const r = await api.post(`/cases/${caseId}/fascicolo-notify/${sessionId}`, {
        art9_consent: art9Consent, privacy_read: privacyRead,
      });
      await downloadPdf();
      if (r?.data?.already_sent) {
        toast(r.data.message || "Il report è già stato inviato al consulente.");
      } else {
        toast.success("Fascicolo inviato al consulente e scaricato");
      }
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio al consulente");
    } finally { setProcessing(false); }
  };

  const chooseDownloadOnly = async () => {
    if (!privacyRead) { toast.error("Conferma la lettura dell'informativa privacy"); return; }
    // Two-step inline confirmation (avoids window.confirm blocked in embedded/mobile browsers).
    if (!confirmPurge) {
      setConfirmPurge(true);
      toast("Clicca di nuovo per confermare: scarichi il fascicolo e i dati verranno eliminati.");
      return;
    }
    setProcessing(true); setChoice("purge");
    try {
      // 1) Download FIRST while data still exists
      const res = await fetch(`${API}/cases/${caseId}/fascicolo-download/${sessionId}`, { credentials: "include" });
      if (res.status === 409) {
        const body = await res.json().catch(() => ({}));
        toast.error(body?.detail?.message
          || "Il fascicolo presenta incoerenze da risolvere prima della generazione del report.");
        setConfirmPurge(false);
        return;
      }
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fascicolo-${caseId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Delay revoke so slower browsers (Firefox, mobile Safari) complete the download.
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      // 2) Then purge
      await api.post(`/cases/${caseId}/fascicolo-purge/${sessionId}`, {
        art9_consent: art9Consent, privacy_read: privacyRead,
      });
      toast.success("Fascicolo scaricato. I dati sono stati eliminati dal sistema.");
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante il download");
      setConfirmPurge(false);
    } finally { setProcessing(false); }
  };

  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <Header />
      <main className="flex-1 max-w-3xl mx-auto px-6 py-12 w-full">
        {status === "checking" && (
          <div className="text-center border hairline bg-white p-10" data-testid="fs-checking">
            <Loader2 className="animate-spin text-navy mx-auto" size={40} />
            <h1 className="font-serif text-2xl font-semibold mt-4">Conferma pagamento…</h1>
            <p className="text-sm text-slate-600 mt-2 font-mono">Tentativo {attempts + 1}/30</p>
          </div>
        )}

        {(status === "timeout" || status === "error") && (
          <div className="text-center border hairline bg-white p-10" data-testid="fs-error">
            <XCircle className="text-forensic-red mx-auto" size={40} />
            <h1 className="font-serif text-2xl font-semibold mt-4">Verifica non riuscita</h1>
            <p className="text-sm text-slate-600 mt-2">
              Se il pagamento è stato addebitato, contatta il consulente per il download.
            </p>
          </div>
        )}

        {status === "paid" && !done && (
          <div data-testid="fs-paid">
            <div className="text-center border hairline bg-white p-8 mb-6">
              <CheckCircle2 className="text-forensic-green mx-auto" size={40} />
              <h1 className="font-serif text-3xl font-semibold mt-3">Pagamento confermato</h1>
              <p className="text-sm text-slate-600 mt-2 max-w-xl mx-auto">
                Il tuo fascicolo Premium è pronto. Prima di scaricarlo indica come vuoi procedere.
                Riceverai la fattura in regime forfettario via email.
              </p>
            </div>

            {/* Consensi obbligatori */}
            <div className="border hairline bg-white p-5 space-y-3 mb-5" data-testid="fs-consents">
              <div className="overline mb-1 flex items-center gap-1"><ShieldCheck size={12} /> Consensi richiesti</div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={privacyRead} onChange={(e) => setPrivacyRead(e.target.checked)}
                  className="mt-1 accent-navy w-4 h-4 shrink-0" data-testid="fs-privacy-check" />
                <span className="text-sm text-slate-700 leading-relaxed">
                  Dichiaro di aver letto l'<a href={PRIVACY_URL} target="_blank" rel="noreferrer"
                    className="text-navy underline font-medium" data-testid="fs-privacy-link">informativa privacy</a> e
                  di prestare il consenso al trattamento dei miei dati personali per le finalità connesse al servizio.
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={art9Consent} onChange={(e) => setArt9Consent(e.target.checked)}
                  className="mt-1 accent-navy w-4 h-4 shrink-0" data-testid="fs-art9-check" />
                <span className="text-sm text-slate-700 leading-relaxed">
                  Consenso <b>ex art. 9 GDPR</b> — Autorizzo il trattamento di eventuali <b>dati particolari</b> (salute,
                  ricoveri, condizioni cliniche) da me dichiarati, limitatamente alle finalità della valutazione
                  documentale del fascicolo grafologico.
                </span>
              </label>
            </div>

            {/* Due opzioni */}
            <div className="grid md:grid-cols-2 gap-4" data-testid="fs-choices">
              <button onClick={chooseShareAndDownload} disabled={processing}
                className="text-left border-2 border-navy bg-navy text-white p-6 hover:bg-navy-dark transition-colors disabled:opacity-60"
                data-testid="fs-share-btn">
                <div className="overline text-white/70 mb-1">Opzione 1 · consigliata</div>
                <div className="font-serif text-xl font-semibold flex items-center gap-2">
                  <Send size={16} /> Scarica + invia anche al consulente
                </div>
                <p className="text-sm text-white/80 mt-3 leading-relaxed">
                  Scarichi il fascicolo Premium e allo stesso tempo lo condividi con il consulente
                  <b> Dott. Alessio Lugliè</b> per richiederne la visione ed essere ricontattato per
                  una <b>prima consulenza gratuita</b>.
                </p>
                {processing && choice === "share" && <Loader2 className="animate-spin mt-3" size={16} />}
              </button>

              <button onClick={chooseDownloadOnly} disabled={processing}
                className={`text-left border-2 p-6 transition-colors disabled:opacity-60 ${
                  confirmPurge
                    ? "border-forensic-red bg-forensic-red/5 hover:bg-forensic-red/10"
                    : "border-slate-300 bg-white hover:border-navy"
                }`}
                data-testid="fs-purge-btn">
                <div className={`overline mb-1 ${confirmPurge ? "text-forensic-red" : "text-slate-500"}`}>
                  {confirmPurge ? "Conferma richiesta" : "Opzione 2"}
                </div>
                <div className="font-serif text-xl font-semibold flex items-center gap-2 text-ink">
                  <Download size={16} /> {confirmPurge ? "Conferma: scarica ed elimina" : "Scarica soltanto"}
                </div>
                <p className="text-sm text-slate-600 mt-3 leading-relaxed">
                  {confirmPurge
                    ? "Clicca di nuovo per confermare. Il PDF viene scaricato ed i dati inseriti vengono eliminati dal sistema. Operazione irreversibile."
                    : <>Scarichi il fascicolo Premium <b>senza</b> inviarlo al consulente.
                        Il report e tutti i dati inseriti verranno <b>eliminati dal sistema</b> subito dopo il download.</>}
                </p>
                {processing && choice === "purge" && <Loader2 className="animate-spin mt-3" size={16} />}
              </button>
            </div>

            <p className="mt-6 text-xs text-slate-500 leading-relaxed max-w-2xl">
              In caso di successivo conferimento incarico al consulente, il costo di € 29,90 sarà detratto
              dall'importo finale. La scelta &quot;Scarica soltanto&quot; non incide sulla fattura già emessa.
            </p>
          </div>
        )}

        {done && (
          <div className="text-center border-2 border-forensic-green bg-forensic-green/5 p-10" data-testid="fs-done">
            <CheckCircle2 className="text-forensic-green mx-auto" size={40} />
            <h2 className="font-serif text-2xl font-semibold mt-3">Operazione completata</h2>
            <p className="text-sm text-slate-600 mt-2">
              {choice === "share"
                ? "Il fascicolo è stato scaricato e condiviso con il consulente. Verrai ricontattato a breve."
                : "Il fascicolo è stato scaricato. I dati inseriti sono stati eliminati dal sistema."}
            </p>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
